import React, { useState, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Lock, Mail, Building2, User, Shield, ShieldCheck } from 'lucide-react';

const Login = memo(() => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'supervisor', // Default to supervisor
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  // const navigate = useNavigate(); // Not used in this component

  const handleChange = useCallback((e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔑 Login: Form submitted', { email: formData.email, role: formData.role });
    console.log('🔑 Login: Form data:', formData);
    console.log('🔑 Login: About to call login function...');
    
    setLoading(true);
    
    try {
      const result = await login(formData.email, formData.password, formData.role);
      console.log('🔑 Login: Login result', result);
      
      if (result.success) {
        console.log('🔑 Login: Login successful, redirect will be handled by AuthContext');
        // The login function will handle the redirect based on the actual user role
        // We don't need to navigate here as the AuthContext will handle it
      } else {
        console.log('🔑 Login: Login failed');
      }
    } catch (error) {
      console.error('🔑 Login: Error in handleSubmit:', error);
      console.error('🔑 Login: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-security-50 via-primary-50 to-secondary-100 px-4 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-security-pattern opacity-5"></div>
      
      {/* Floating Security Icons */}
      <div className="absolute top-20 left-20 text-security-200 animate-pulse-soft">
        <ShieldCheck size={40} />
      </div>
      <div className="absolute bottom-20 right-20 text-primary-200 animate-pulse-soft" style={{animationDelay: '1s'}}>
        <Building2 size={40} />
      </div>
      
      <div className="max-w-md w-full space-y-6 sm:space-y-8 relative z-10 px-4 sm:px-0">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 sm:h-20 sm:w-20 mb-4 sm:mb-6">
            <img 
              src="/assets/logo.png" 
              alt="Rajput Security Logo" 
              className="h-full w-full object-contain"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-secondary-900 mb-2 sm:mb-3 font-display">
            Rajput Security
          </h1>
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-secondary-700 mb-1 sm:mb-2">
            Management System
          </h2>
          <p className="text-sm sm:text-base text-secondary-600">
            Sign in to your secure dashboard
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection */}
            <div className="form-group">
              <label className="form-label">
                Login As
              </label>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'supervisor' })}
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 min-h-[60px] sm:min-h-[70px] ${
                    formData.role === 'supervisor'
                      ? 'border-security-500 bg-security-50 text-security-700 shadow-glow-security'
                      : 'border-secondary-200 bg-white text-secondary-600 hover:border-secondary-300 hover:shadow-soft'
                  }`}
                >
                  <User className="h-4 w-4 sm:h-5 sm:w-5 mb-1 sm:mb-2" />
                  <span className="font-medium text-xs sm:text-sm text-center leading-tight">Supervisor</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'admin' })}
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 min-h-[60px] sm:min-h-[70px] ${
                    formData.role === 'admin'
                      ? 'border-security-500 bg-security-50 text-security-700 shadow-glow-security'
                      : 'border-secondary-200 bg-white text-secondary-600 hover:border-secondary-300 hover:shadow-soft'
                  }`}
                >
                  <Shield className="h-4 w-4 sm:h-5 sm:w-5 mb-1 sm:mb-2" />
                  <span className="font-medium text-xs sm:text-sm text-center leading-tight">Admin</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'employee' })}
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 min-h-[60px] sm:min-h-[70px] ${
                    formData.role === 'employee'
                      ? 'border-security-500 bg-security-50 text-security-700 shadow-glow-security'
                      : 'border-secondary-200 bg-white text-secondary-600 hover:border-secondary-300 hover:shadow-soft'
                  }`}
                >
                  <User className="h-4 w-4 sm:h-5 sm:w-5 mb-1 sm:mb-2" />
                  <span className="font-medium text-xs sm:text-sm text-center leading-tight">Employee</span>
                </button>
              </div>
            </div>

            {/* Email/Employee Code Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                {formData.role === 'employee' ? 'Email or Employee Code' : 'Email Address'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center justify-center pointer-events-none">
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-secondary-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type={formData.role === 'employee' ? 'text' : 'email'}
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 pl-10 sm:pl-12 pr-4 border-2 border-secondary-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 bg-white shadow-soft text-base"
                  placeholder={formData.role === 'employee' ? 'Enter email or employee code' : 'Enter your email'}
                />
              </div>
              {formData.role === 'employee' && (
                <p className="text-sm text-secondary-500 mt-1">
                  You can use either your email address or employee code to log in
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center justify-center pointer-events-none">
                  <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-secondary-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 pl-10 sm:pl-12 pr-12 border-2 border-secondary-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300 bg-white shadow-soft text-base"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center justify-center text-secondary-400 hover:text-secondary-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Eye className="h-4 w-4 sm:h-5 sm:w-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-security-600 to-security-700 hover:from-security-700 hover:to-security-800 text-white font-semibold py-3 sm:py-4 px-6 rounded-xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-security-500/20 shadow-soft hover:shadow-medium transform hover:-translate-y-0.5 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => console.log('🔑 Login: Submit button clicked')}
            >
              {loading ? (
                <div className="loading-dots">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="text-sm sm:text-base">Sign In</span>
                </>
              )}
            </button>

            {/* Test Button - Remove this after debugging */}
            {/* <button
              type="button"
              onClick={() => {
                console.log('🔑 Login: Test button clicked');
                console.log('🔑 Login: Current form data:', formData);
                console.log('🔑 Login: About to test login function...');
                login('test@example.com', 'test123', 'employee');
              }}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg mt-2 w-full"
            >
              Test Login (Debug)
            </button> */}
          </form>

          {/* Forgot Password Link */}
          <div className="mt-6 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
            >
              Forgot your password?
            </Link>
          </div>
        </div>

        {/* Security Notice */}
        <div className="text-center">
          <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-security-50 border border-security-200 rounded-xl">
            <ShieldCheck className="h-3 w-3 sm:h-4 sm:w-4 text-security-600 mr-2" />
            <span className="text-xs sm:text-sm text-security-700 font-medium">
              Secure & Encrypted Connection
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Login;
